'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import RagCanvas from './RagCanvas';
import type {
  RagNode,
  RagConnection,
  KnowledgeGraphNode,
  KnowledgeGraph,
  HierarchicalNode,
} from './ragCanvas.types';

// const knowledgeGraph = {
//   nodes: [
//     {
//       id: 'macro_analysis',
//       label: 'Macro Analysis Framework',
//       type: 'Concept',
//       description: 'Framework for determining market conditions based on macro factors',
//     },
//     {
//       id: 'monetary_cycle',
//       label: 'Monetary Cycle',
//       type: 'Topic',
//       description: 'Impact of interest rates and liquidity on market cycles',
//     },
//     {
//       id: 'economic_cycle',
//       label: 'Economic Cycle',
//       type: 'Topic',
//       description: 'Current stage in the economic cycle and its impact',
//     },
//     {
//       id: 'regulatory_cycle',
//       label: 'Regulatory Cycle',
//       type: 'Topic',
//       description: 'Impact of regulatory clarity on market cycles',
//     },
//     {
//       id: 'narrative_drivers',
//       label: 'Narrative Drivers',
//       type: 'Topic',
//       description: 'Market focus and main themes over specific periods',
//     },
//     {
//       id: 'catalyst_drivers',
//       label: 'Catalyst Drivers',
//       type: 'Topic',
//       description: 'Specific events or data points triggering volatility',
//     },
//     {
//       id: 'federal_reserve',
//       label: 'Federal Reserve',
//       type: 'Entity',
//       description: 'Central bank influencing monetary policy',
//     },
//     {
//       id: 'inflation',
//       label: 'Inflation',
//       type: 'Concept',
//       description: 'Rate of increase in prices',
//     },
//     {
//       id: 'employment',
//       label: 'Employment',
//       type: 'Concept',
//       description: 'Level of job availability and labor market health',
//     },
//     {
//       id: 'liquidity',
//       label: 'Liquidity',
//       type: 'Concept',
//       description: 'Availability of liquid assets in the market',
//     },
//     {
//       id: 'merrill_lynch_clock',
//       label: 'Merrill Lynch Investment Clock',
//       type: 'Concept',
//       description: 'Framework relating economic cycles to asset performance',
//     },
//     {
//       id: 'leading_indicators',
//       label: 'Leading Indicators',
//       type: 'Topic',
//       description: 'Indicators that predict future economic activity',
//     },
//     {
//       id: 'coincident_indicators',
//       label: 'Coincident Indicators',
//       type: 'Topic',
//       description: 'Indicators that reflect current economic activity',
//     },
//     {
//       id: 'lagging_indicators',
//       label: 'Lagging Indicators',
//       type: 'Topic',
//       description: 'Indicators that confirm past economic activity',
//     },
//     {
//       id: 'contrarian_thinking',
//       label: 'Contrarian Thinking',
//       type: 'Concept',
//       description: 'Challenging market consensus and forming independent judgment',
//     },
//   ],
//   edges: [
//     {
//       source: 'macro_analysis',
//       target: 'monetary_cycle',
//       type: 'is_part_of',
//       label: 'Includes',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'economic_cycle',
//       type: 'is_part_of',
//       label: 'Includes',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'regulatory_cycle',
//       type: 'is_part_of',
//       label: 'Includes',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'narrative_drivers',
//       type: 'is_part_of',
//       label: 'Includes',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'catalyst_drivers',
//       type: 'is_part_of',
//       label: 'Includes',
//     },
//     {
//       source: 'monetary_cycle',
//       target: 'federal_reserve',
//       type: 'influences',
//       label: 'Influenced by',
//     },
//     {
//       source: 'federal_reserve',
//       target: 'inflation',
//       type: 'influences',
//       label: 'Influences',
//     },
//     {
//       source: 'federal_reserve',
//       target: 'employment',
//       type: 'influences',
//       label: 'Influences',
//     },
//     {
//       source: 'federal_reserve',
//       target: 'liquidity',
//       type: 'influences',
//       label: 'Influences',
//     },
//     {
//       source: 'economic_cycle',
//       target: 'merrill_lynch_clock',
//       type: 'relates_to',
//       label: 'Related to',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'leading_indicators',
//       type: 'relates_to',
//       label: 'Uses',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'coincident_indicators',
//       type: 'relates_to',
//       label: 'Uses',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'lagging_indicators',
//       type: 'relates_to',
//       label: 'Uses',
//     },
//     {
//       source: 'macro_analysis',
//       target: 'contrarian_thinking',
//       type: 'relates_to',
//       label: 'Employs',
//     },
//   ],
// };

/**
 * 处理知识图谱：限制子节点数（保留深层），并按层级结构输出
 * 包含循环引用检测和最大深度限制，防止内存泄漏
 */
function transformGraphToHierarchy(
  graph: KnowledgeGraph,
  maxChildren = 5,
  maxDepth = 50,
): HierarchicalNode[] {
  const { nodes, edges } = graph;

  // 1. 建立邻接表
  const adj: Record<string, string[]> = {};
  const nodeDataMap = new Map<string, KnowledgeGraphNode>();
  const hasParent = new Set<string>();

  nodes.forEach((node) => nodeDataMap.set(node.id, { ...node }));

  edges.forEach(({ source, target }) => {
    if (!adj[source]) adj[source] = [];
    adj[source].push(target);
    hasParent.add(target);
  });

  // 2. 计算最大深度（用于剪枝决策）- 带循环检测
  const depthCache = new Map<string, number>();
  const depthVisiting = new Set<string>(); // 追踪当前访问路径

  function getSubtreeDepth(nodeId: string): number {
    // 检测循环引用
    if (depthVisiting.has(nodeId)) {
      console.warn(`Circular reference detected in depth calculation at node: ${nodeId}`);
      return 0; // 返回0深度，避免循环
    }

    if (depthCache.has(nodeId)) return depthCache.get(nodeId)!;

    const children = adj[nodeId] || [];
    if (children.length === 0) return 0;

    // 标记当前节点为正在访问
    depthVisiting.add(nodeId);

    try {
      const childDepths = children.map((id) => getSubtreeDepth(id));
      const depth = childDepths.length > 0 ? 1 + Math.max(...childDepths) : 0;
      depthCache.set(nodeId, depth);
      return depth;
    } finally {
      // 访问完成后移除标记
      depthVisiting.delete(nodeId);
    }
  }

  // 3. 递归构建层级结构并限制子节点 - 带循环检测和深度限制
  const buildVisited = new Set<string>(); // 全局访问记录，防止重复节点

  function buildNode(
    nodeId: string,
    currentDepth = 0,
    ancestors = new Set<string>(),
  ): HierarchicalNode | null {
    // 深度限制检查
    if (currentDepth >= maxDepth) {
      console.warn(`Max depth (${maxDepth}) reached at node: ${nodeId}`);
      const nodeData = nodeDataMap.get(nodeId);
      return nodeData ? { ...nodeData } : null;
    }

    // 循环引用检查：检查祖先路径
    if (ancestors.has(nodeId)) {
      console.warn(`Circular reference detected in build at node: ${nodeId}`);
      return null; // 返回null，跳过这个循环引用
    }

    // 检查是否已经访问过（避免在不同分支重复构建同一节点）
    if (buildVisited.has(nodeId)) {
      console.warn(`Node ${nodeId} already built in another branch, skipping to avoid duplication`);
      return null;
    }

    const nodeData = nodeDataMap.get(nodeId);
    if (!nodeData) {
      console.error(`Node ${nodeId} not found in node data map`);
      return null;
    }

    // 标记为已访问
    buildVisited.add(nodeId);

    const nodeInfo: HierarchicalNode = { ...nodeData };
    let childrenIds = adj[nodeId] || [];

    if (childrenIds.length > 0) {
      // 如果超过限制，按子树深度降序排列，保留深层分支
      if (childrenIds.length > maxChildren) {
        try {
          childrenIds.sort((a, b) => getSubtreeDepth(b) - getSubtreeDepth(a));
          childrenIds = childrenIds.slice(0, maxChildren);
        } catch (error) {
          console.error(`Error sorting children for node ${nodeId}:`, error);
          childrenIds = childrenIds.slice(0, maxChildren); // 降级处理
        }
      }

      // 创建新的祖先集合，包含当前节点
      const newAncestors = new Set(ancestors);
      newAncestors.add(nodeId);

      // 递归构建子节点，过滤掉null值（循环引用或错误）
      const subNodes = childrenIds
        .map((id) => buildNode(id, currentDepth + 1, newAncestors))
        .filter((node): node is HierarchicalNode => node !== null);

      if (subNodes.length > 0) {
        nodeInfo.sub_nodes = subNodes;
      }
    }

    return nodeInfo;
  }

  // 4. 寻找根节点并输出
  const roots = nodes.filter((n) => !hasParent.has(n.id));

  if (roots.length === 0) {
    console.warn('No root nodes found in graph. Possible circular graph structure.');
    // 如果没有根节点（可能整个图是一个循环），选择第一个节点作为根
    if (nodes.length > 0) {
      return [buildNode(nodes[0].id, 0, new Set())].filter(
        (node): node is HierarchicalNode => node !== null,
      );
    }
    return [];
  }

  return roots
    .map((root) => buildNode(root.id, 0, new Set()))
    .filter((node): node is HierarchicalNode => node !== null);
}

// 执行转换
// const structuredResult = transformGraphToHierarchy(knowledgeGraph, 5);
// console.log(structuredResult);

// 示例：如何使用RagCanvas组件
function RagCanvasContainer({ knowledgeGraph }: { knowledgeGraph: KnowledgeGraph }) {
  const [nodes, setNodes] = useState<RagNode[]>([]);
  const [connections, setConnections] = useState<RagConnection[]>([]);
  const [presetConnections, setPresetConnections] = useState<RagConnection[]>([]);
  const [animationProgress, setAnimationProgress] = useState<Map<string, number>>(new Map());
  const [rootId, setRootId] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationFrameRef = useRef<number | undefined>(undefined);

  // 生成节点数据
  const generateNodes = () => {
    const CANVAS_WIDTH = 800;
    const CANVAS_HEIGHT = 300;
    const MAX_NODES = 60;
    const MIN_RADIUS = 2;
    const MAX_RADIUS = 6;

    const nodeList = [];
    const rows = Math.ceil((CANVAS_HEIGHT - 80) / 52.5) + 1;
    const cols = Math.ceil((CANVAS_WIDTH - 80) / (35 * Math.sqrt(3))) + 1;
    let count = 0;

    for (let row = 0; row < rows && count < MAX_NODES; row++) {
      for (let col = 0; col < cols && count < MAX_NODES; col++) {
        const x = 40 + col * 35 * Math.sqrt(3) + ((row % 2) * 35 * Math.sqrt(3)) / 2;
        const y = 40 + row * 35 * 1.5;

        if (x >= 40 && x <= CANVAS_WIDTH - 40 && y >= 40 && y <= CANVAS_HEIGHT - 40) {
          nodeList.push({
            id: count,
            x: x + (Math.random() - 0.5) * 4,
            y: y + (Math.random() - 0.5) * 4,
            radius: MIN_RADIUS + Math.random() * (MAX_RADIUS - MIN_RADIUS),
            isHighlighted: false,
            row: row,
            col: col,
          });
          count++;
        }
      }
    }

    return nodeList;
  };

  const result = transformGraphToHierarchy(knowledgeGraph);

  // 生成连接数据
  const generateConnections = (
    nodeList: RagNode[],
  ): { connections: RagConnection[]; rootId: number } => {
    // const result = transformGraphToHierarchy(knowledgeGraph);
    const nodeConnections: RagConnection[] = [];
    const usedIds = new Set<number>();

    // 获取邻居节点
    const getNeighbors = (node: RagNode, allNodes: RagNode[]): RagNode[] => {
      const neighbors: RagNode[] = [];
      const { row, col } = node;
      if (row === undefined || col === undefined) return neighbors;
      const offsets =
        row % 2 === 0
          ? [
              { dr: -1, dc: -1 },
              { dr: -1, dc: 0 },
              { dr: 0, dc: -1 },
              { dr: 0, dc: 1 },
              { dr: 1, dc: -1 },
              { dr: 1, dc: 0 },
            ]
          : [
              { dr: -1, dc: 0 },
              { dr: -1, dc: 1 },
              { dr: 0, dc: -1 },
              { dr: 0, dc: 1 },
              { dr: 1, dc: 0 },
              { dr: 1, dc: 1 },
            ];

      offsets.forEach((offset) => {
        const neighbor = allNodes.find(
          (n: RagNode) => n.row === row + offset.dr && n.col === col + offset.dc,
        );
        if (neighbor && !usedIds.has(neighbor.id)) {
          neighbors.push(neighbor);
        }
      });

      return neighbors;
    };

    // 使用层级结构来分配节点
    const nodeIdMap = new Map<string, number>(); // 知识图谱节点ID -> RagNode ID
    let currentNodeIndex = 0;

    // 递归遍历层级结构，为每个节点分配RagNode并创建连接
    const assignNodes = (
      hierarchicalNode: HierarchicalNode,
      parentRagNodeId?: number,
      level = 0,
    ) => {
      if (currentNodeIndex >= nodeList.length) return;

      const ragNodeId = currentNodeIndex;
      nodeIdMap.set(hierarchicalNode.id, ragNodeId);
      usedIds.add(ragNodeId);
      currentNodeIndex++;

      // 如果有父节点，创建连接
      if (parentRagNodeId !== undefined) {
        nodeConnections.push({
          from: parentRagNodeId,
          to: ragNodeId,
          level: level,
          node: hierarchicalNode,
        });
      }

      // 递归处理子节点
      if (hierarchicalNode.sub_nodes && hierarchicalNode.sub_nodes.length > 0) {
        // 获取可用的邻居节点
        const currentRagNode = nodeList[ragNodeId];
        const availableNeighbors = getNeighbors(currentRagNode, nodeList);

        hierarchicalNode.sub_nodes.forEach((childNode, index) => {
          if (availableNeighbors[index]) {
            // 使用邻居节点的索引
            const neighborId = availableNeighbors[index].id;
            currentNodeIndex = neighborId;
          }
          assignNodes(childNode, ragNodeId, level + 1);
        });
      }
    };

    // 从第一个根节点开始
    const max = 34;
    const min = 27;
    if (result.length > 0) {
      const rootIndex = Math.floor(Math.random() * (max - min + 1)) + min;
      currentNodeIndex = rootIndex;
      assignNodes(result[0]);
      return { connections: nodeConnections, rootId: rootIndex };
    }

    // 如果没有结果，返回默认值
    return { connections: [], rootId: 0 };
  };

  // 生成预设连接（网格）
  const generatePresetConnections = (nodeList: RagNode[]): RagConnection[] => {
    const preset: RagConnection[] = [];
    const used = new Set<string>();

    nodeList.forEach((node: RagNode) => {
      const { row, col } = node;
      if (row === undefined || col === undefined) return;
      const offsets =
        row % 2 === 0
          ? [
              { dr: -1, dc: -1 },
              { dr: -1, dc: 0 },
              { dr: 0, dc: -1 },
              { dr: 0, dc: 1 },
              { dr: 1, dc: -1 },
              { dr: 1, dc: 0 },
            ]
          : [
              { dr: -1, dc: 0 },
              { dr: -1, dc: 1 },
              { dr: 0, dc: -1 },
              { dr: 0, dc: 1 },
              { dr: 1, dc: 0 },
              { dr: 1, dc: 1 },
            ];

      offsets.forEach((offset) => {
        const neighbor = nodeList.find(
          (n: RagNode) => n.row === row + offset.dr && n.col === col + offset.dc,
        );
        if (neighbor) {
          const key = [node.id, neighbor.id].sort((a, b) => a - b).join('-');
          if (!used.has(key)) {
            preset.push({ from: node.id, to: neighbor.id, level: -1 });
            used.add(key);
          }
        }
      });
    });

    return preset;
  };

  // 初始化
  const initialize = () => {
    const nodeList = generateNodes();
    const { connections: conns, rootId: root } = generateConnections(nodeList);
    const presets = generatePresetConnections(nodeList);

    setNodes(nodeList);
    setConnections(conns);
    setPresetConnections(presets);
    setRootId(root);
    setAnimationProgress(new Map());
  };

  // 运行动画
  const runAnimation = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    const progress = new Map<string, number>();
    const activeConnections = [...connections];
    const duration = 500; // 500ms
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);

      activeConnections.forEach((conn: RagConnection) => {
        const key = `${conn.from}-${conn.to}`;
        progress.set(key, t);
      });

      // 更新节点高亮状态
      const updatedNodes = nodes.map((node: RagNode) => ({
        ...node,
        isHighlighted:
          activeConnections.some((c: RagConnection) => c.to === node.id) || node.id === rootId,
      }));

      setNodes(updatedNodes);
      setAnimationProgress(new Map(progress));

      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animate();
  };

  useEffect(() => {
    initialize();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当数据准备好后自动运行动画
  useEffect(() => {
    if (connections.length > 0 && !isAnimating) {
      runAnimation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections]);

  return (
    <div className="flex flex-col items-center gap-6">
      <RagCanvas
        nodes={nodes}
        connections={connections}
        presetConnections={presetConnections}
        animationProgress={animationProgress}
        rootId={rootId}
        rootNode={result.length > 0 ? result[0] : null}
        onNodeHover={(node) => {
          console.log('Hovered node:', node);
        }}
      />
    </div>
  );
}

export default RagCanvasContainer;
